import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Send, Monitor, Smartphone, Globe, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiJson } from "@/lib/api";
import ThinkingPulse from "@/components/ThinkingPulse";
import AtelierCanvas from "@/components/website-templates/AtelierCanvas";
import type { SiteSpec } from "@/components/website-templates/AtelierCanvas";
import { getThemeById } from "@/components/website-templates/templateThemes";

interface Msg { role: string; content: string; modifie?: boolean }
interface Site extends SiteSpec { messages: Msg[]; slug?: string | null; published?: boolean }

const SUGGESTIONS = ["Rends le titre plus grand", "Utilise une police plus élégante", "Change la couleur principale", "Reformule l'accroche", "Change le texte du bouton"];

export default function Atelier() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wanted = params.get("template") || "";
  const [site, setSite] = useState<Site | null>(null);
  const [state, setState] = useState<"chargement" | "creation" | "pret" | "erreur">("chargement");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState<"ordinateur" | "mobile">("ordinateur");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const init = useCallback(async () => {
    try {
      const cur = await apiJson<{ site: Site | null }>("/api/atelier");
      const existing = cur.site && cur.site.template_id && !cur.site.template_id.startsWith("immo-") ? cur.site : null;
      if (wanted && (!existing || existing.template_id !== wanted)) {
        if (existing && !window.confirm("Remplacer votre site actuel par ce nouveau modèle ?")) { setSite(existing); setState("pret"); return; }
        setState("creation");
        const r = await apiJson<{ site: Site }>("/api/atelier/start", { method: "POST", body: JSON.stringify({ template_id: wanted }) });
        setSite(r.site); setState("pret");
      } else if (existing) { setSite(existing); setState("pret"); }
      else { setState("erreur"); }
    } catch { setState("erreur"); }
  }, [wanted]);
  useEffect(() => { if (user) void init(); }, [user, init]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [site?.messages.length, busy]);

  const send = async (text?: string) => {
    const m = (text ?? input).trim();
    if (!m || busy || !site) return;
    setInput(""); setBusy(true);
    setSite({ ...site, messages: [...site.messages, { role: "user", content: m }] });
    try { const r = await apiJson<{ site: Site; modifie: boolean }>("/api/atelier/chat", { method: "POST", body: JSON.stringify({ message: m }) }); setSite(r.site); }
    catch { toast.error("Grafton n'a pas pu répondre, réessayez."); }
    finally { setBusy(false); }
  };

  const publier = async (pub: boolean) => {
    try { const r = await apiJson<{ site: Site }>("/api/atelier/publish", { method: "POST", body: JSON.stringify({ published: pub }) }); setSite(r.site); toast.success(pub ? "Votre site est publié" : "Votre site n'est plus public"); }
    catch { toast.error("Publication impossible"); }
  };

  const adresse = site?.slug ? `https://${site.slug}.graftonpartenaire.fr` : "";
  const theme = site ? getThemeById(site.template_id) : null;

  if (state === "chargement" || state === "creation")
    return <div className="min-h-screen flex items-center justify-center bg-background"><ThinkingPulse label={state === "creation" ? "Grafton prépare votre site à partir de votre activité…" : "Chargement…"} /></div>;
  if (state === "erreur" || !site)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <p className="text-sm text-muted-foreground">Aucun site en cours, ou la préparation a échoué.</p>
        <Button onClick={() => navigate("/modeles")}>Choisir un modèle</Button>
      </div>
    );

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" data-testid="atelier">
      <div className="h-12 shrink-0 border-b border-border/50 bg-card/80 flex items-center justify-between px-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("/modeles")}><ArrowLeft className="h-4 w-4" />Modèles</Button>
          <span className="text-sm font-semibold truncate">{site.name}</span>
          {theme && <Badge variant="outline" className="hidden md:inline-flex text-[10px]">{theme.name}</Badge>}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="icon" variant={device === "ordinateur" ? "default" : "ghost"} className="h-8 w-8" onClick={() => setDevice("ordinateur")} aria-label="Ordinateur"><Monitor className="h-4 w-4" /></Button>
          <Button size="icon" variant={device === "mobile" ? "default" : "ghost"} className="h-8 w-8" onClick={() => setDevice("mobile")} aria-label="Mobile"><Smartphone className="h-4 w-4" /></Button>
          {site.published
            ? <><Button size="sm" variant="outline" className="gap-1" onClick={() => { void navigator.clipboard.writeText(adresse); toast.success("Adresse copiée"); }}><Copy className="h-3.5 w-3.5" />Copier l'adresse</Button><Button size="sm" variant="ghost" onClick={() => publier(false)}>Dépublier</Button></>
            : <Button size="sm" className="gap-1" onClick={() => publier(true)} data-testid="publier"><Globe className="h-3.5 w-3.5" />Publier</Button>}
        </div>
      </div>
      {site.published && <div className="shrink-0 bg-emerald-500/10 border-b border-emerald-500/30 px-3 py-1.5 text-xs flex items-center gap-2">Publié à l'adresse <a className="underline inline-flex items-center gap-1" href={adresse} target="_blank" rel="noreferrer">{adresse}<ExternalLink className="h-3 w-3" /></a></div>}

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div className="flex-1 min-h-0 bg-muted/30 overflow-auto p-3">
          <div className={`mx-auto bg-background shadow-xl rounded-lg overflow-hidden border border-border/60 ${device === "mobile" ? "w-[390px] max-w-full" : "w-full"}`}>
            <AtelierCanvas spec={site} />
          </div>
        </div>
        <div className="lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-border/50 flex flex-col h-[42vh] lg:h-auto bg-card/40">
          <div className="p-3 border-b border-border/50"><p className="text-sm font-semibold">Dites à Grafton ce que vous voulez changer</p><p className="text-xs text-muted-foreground">Textes, couleurs, police, taille des textes, bouton. Chaque changement s'affiche tout de suite.</p></div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="atelier-chat">
            {site.messages.length === 0 && <div className="flex flex-wrap gap-1.5">{SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-xs px-2.5 py-1.5 rounded-full border border-border/60 hover:border-primary/50 text-muted-foreground">{s}</button>)}</div>}
            {site.messages.map((m, i) => <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[92%] ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-secondary/60 border border-border/50"}`}>{m.content}{m.role === "assistant" && m.modifie && <span className="block text-[10px] text-emerald-500 mt-1">Modification appliquée</span>}</div>)}
            {busy && <ThinkingPulse />}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-border/50 flex gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} className="text-sm" placeholder="Ex : rends le titre plus grand et mets le bouton en vert" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} data-testid="atelier-input" />
            <Button size="icon" className="self-end" disabled={busy || !input.trim()} onClick={() => send()}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
